import React, { useState, useRef, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { BellIcon } from "@heroicons/react/24/outline";
import { BellAlertIcon } from "@heroicons/react/24/solid";
import { CheckIcon, TrashIcon } from "@heroicons/react/24/outline";
import type { NotificationCenterProps, Notification } from "../types/NotificationCenter.types";

export const NotificationCenter: React.FC<NotificationCenterProps> = ({
  notifications,
  unreadCount,
  loading = false,
  onMarkAsRead,
  onMarkAllAsRead,
  onDelete,
  onNotificationClick,
}) => {
  const { t } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
      }
    }

    if (isOpen) {
      document.addEventListener("mousedown", handleClickOutside);
      return () =>
        document.removeEventListener("mousedown", handleClickOutside);
    }
  }, [isOpen]);

  const formatTimeAgo = (dateString: string): string => {
    const date = new Date(dateString);
    const now = new Date();
    const seconds = Math.floor((now.getTime() - date.getTime()) / 1000);

    if (seconds < 60) return t("notificationCenter.timeAgo.now");
    if (seconds < 3600)
      return t("notificationCenter.timeAgo.minutes", {
        count: Math.floor(seconds / 60),
      });
    if (seconds < 86400)
      return t("notificationCenter.timeAgo.hours", {
        count: Math.floor(seconds / 3600),
      });
    if (seconds < 604800)
      return t("notificationCenter.timeAgo.days", {
        count: Math.floor(seconds / 86400),
      });
    return date.toLocaleDateString();
  };

  const getNotificationIcon = (type: string): string => {
    const icons: Record<string, string> = {
      consultation_new: "💼",
      consultation_accepted: "✅",
      consultation_rejected: "❌",
      consultation_completed: "🎉",
      case_assigned: "📋",
      case_updated: "📝",
      document_generated: "📄",
      document_reviewed: "👁️",
      review_received: "⭐",
      payment_received: "💰",
      simulation_graded: "📊",
      badge_earned: "🏆",
      message_received: "💬",
      system: "ℹ️",
      reminder: "⏰",
    };
    return icons[type] || "📢";
  };

  const handleNotificationClick = (notification: Notification) => {
    if (!notification.is_read) {
      onMarkAsRead(notification.id);
    }
    onNotificationClick(notification);
    setIsOpen(false);
  };

  return (
    <div className="relative" ref={dropdownRef}>
      {/* Bell Button */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="relative p-2 text-tiber-600 hover:text-tiber-800 transition-colors rounded-lg hover:bg-tiber-50"
        aria-label={t("notificationCenter.ariaLabel")}
      >
        {unreadCount > 0 ? (
          <BellAlertIcon className="h-6 w-6 text-tiber-800" />
        ) : (
          <BellIcon className="h-6 w-6" />
        )}

        {unreadCount > 0 && (
          <span className="absolute top-0 right-0 inline-flex items-center justify-center px-2 py-1 text-xs font-bold leading-none text-white transform translate-x-1/2 -translate-y-1/2 bg-red-500 rounded-full">
            {unreadCount > 99 ? "99+" : unreadCount}
          </span>
        )}
      </button>

      {/* Dropdown */}
      {isOpen && (
        <div className="absolute right-0 mt-2 w-[calc(100vw-2rem)] sm:w-80 md:w-96 max-w-96 bg-white rounded-lg shadow-xl border border-tiber-600 z-50 max-h-[70vh] sm:max-h-[600px] flex flex-col overflow-hidden">
          {/* Header */}
          <div className="px-4 py-3 border-b border-tiber-600 flex items-center justify-between">
            <h3 className="text-lg font-semibold text-tiber-900">
              {t("notificationCenter.title")}
            </h3>
            {unreadCount > 0 && (
              <button
                onClick={onMarkAllAsRead}
                className="text-sm text-tiber-800 hover:text-tiber-900 font-medium"
              >
                {t("notificationCenter.markAllAsRead")}
              </button>
            )}
          </div>

          {/* Notifications List */}
          <div className="overflow-y-auto flex-1">
            {loading ? (
              <div className="p-8 text-center">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-tiber-800 mx-auto"></div>
                <p className="text-sm text-tiber-600 mt-2">
                  {t("common.loading")}
                </p>
              </div>
            ) : notifications.length === 0 ? (
              <div className="p-8 text-center">
                <BellIcon className="h-12 w-12 text-tiber-600 mx-auto mb-3" />
                <p className="text-sm text-tiber-600">
                  {t("notificationCenter.noNotifications")}
                </p>
              </div>
            ) : (
              <div className="divide-y divide-tiber-600">
                {notifications.map((notification) => (
                  <div
                    key={notification.id}
                    className={`
                      px-4 py-3 hover:bg-tiber-50 transition-colors cursor-pointer relative group
                      ${!notification.is_read ? "bg-tiber-50/50" : ""}
                    `}
                  >
                    <div onClick={() => handleNotificationClick(notification)}>
                      <div className="flex items-start gap-3">
                        {/* Icon */}
                        <span className="text-2xl shrink-0 mt-0.5">
                          {getNotificationIcon(notification.type)}
                        </span>

                        {/* Content */}
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium text-tiber-900 mb-0.5">
                            {notification.title}
                          </p>
                          <p className="text-sm text-tiber-600 line-clamp-2">
                            {notification.message}
                          </p>
                          <p className="text-xs text-tiber-500 mt-1">
                            {formatTimeAgo(notification.created_at)}
                          </p>
                        </div>

                        {/* Unread Indicator */}
                        {!notification.is_read && (
                          <div className="shrink-0">
                            <div className="h-2 w-2 bg-tiber-800 rounded-full"></div>
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Actions (show on hover) */}
                    <div className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 transition-opacity flex gap-1">
                      {!notification.is_read && (
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            onMarkAsRead(notification.id);
                          }}
                          className="p-1 bg-white rounded hover:bg-tiber-100 text-tiber-600 hover:text-tiber-800"
                          title={t("notificationCenter.markAsRead")}
                        >
                          <CheckIcon className="h-4 w-4" />
                        </button>
                      )}
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          onDelete(notification.id);
                        }}
                        className="p-1 bg-white rounded hover:bg-red-50 text-tiber-600 hover:text-red-600"
                        title={t("common.delete")}
                      >
                        <TrashIcon className="h-4 w-4" />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Footer */}
          {notifications.length > 0 && (
            <div className="px-4 py-3 border-t border-tiber-200">
              <button
                onClick={() => {
                  // Navigate to all notifications page
                  setIsOpen(false);
                }}
                className="text-sm text-tiber-800 hover:text-tiber-900 font-medium w-full text-center"
              >
                {t("notificationCenter.viewAll")}
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
};
